// 카메라 리그: 3/4 추적 카메라 + 타격 순간 단발 펀치(지속 흔들림 없음) + 전투 연출 컷(cine.ts).
// 2026-10-14 콘셉트 맞춤: 기본 구도는 concept/ui/ui-battle-hud(낮은 각도·넓은 화각·원경 보임),
// 휠로 멀리 빼면 concept/combat/c-mob-sweep(넓은 전장)까지. 기본 각도는 고정이고, 사용자 높이 보정(±6°)과
// 연출 컷의 짧은 피치·궤도 변화만 더한다(빌보드·바닥 데칼이 기본 피치를 쓰므로 작게 둔다).
import * as THREE from 'three/webgpu';
import { CAMERA, CAMERA_PUNCH, CAMERA_ZOOM } from '../shared/config';
import { CAMERA_PREFS_EVENT, CINE_SCALE, SHAKE_SCALE, clampZoom, loadCameraPrefs, updateCameraPrefs, wrapCameraYaw, type CameraPrefs } from '../shared/cameraPrefs';
import { Ev, HitFlag, type GameEvent } from '../shared/events';
import { cameraYaw } from '../shared/cameraYaw';
import { CineDirector, SHOTS, type ShotId } from './cine';
import { OCCLUDER_SLOTS, setCutawayBoss, setCutawayDirection, setCutawayRange, setOccluderFade } from './env/cut';
import { CameraCollider, OCC_MAX } from './cameraCollider';
import { damp } from './util';

/**
 * 보스전 구도: 앞뒤 시선 비율·이동 상한, 최소 거리. 옆 방향은 둘의 중간을 잡는다.
 * 키 7m 고목과 영웅의 발이 함께 보이도록 최소 28m까지 빼고, 먼 방 입구·역방향에서는 화면 경계로 더 벌린다.
 * 사용자 줌은 아래 BOSS_BOX(안전 상자)를 통해 이 거리를 바꾼다(줌 0 = 약 22m, 줌 1 = 약 31m, 1280x720 기준).
 */
const BOSS_FRAME = { toward: 0.62, distance: 28, maxShift: 6.5 } as const;
/**
 * 보스전 안전 상자: 영웅·보스 몸이 화면에서 쓸 수 있는 범위(NDC 절댓값 비율). 줌 1(멀리) = 기존 HUD 안전영역(v 아래, top 위, h 좌우),
 * 줌 0(가까이) = 느슨한 상자. 사용자 줌이 보스방에서도 거리를 바꾸게 하되 줌인 최대에서도 둘의 몸 전체가 화면 안에 남는다.
 * 2026-10-20: 기존에는 줌과 무관한 고정 상자(안전 하한 약 38m)가 사용자 줌을 덮어써 보스방에서 줌인이 먹지 않았다.
 */
export const BOSS_BOX = { far: { v: 0.54, top: 0.5, h: 0.7 }, near: { v: 0.72, top: 0.8, h: 0.9 } } as const;
/** 줌(0..1)에 따른 안전 상자 */
export function bossBox(zoom: number): { v: number; top: number; h: number } {
  const z = Math.min(1, Math.max(0, zoom)), n = BOSS_BOX.near, f = BOSS_BOX.far;
  return { v: n.v + (f.v - n.v) * z, top: n.top + (f.top - n.top) * z, h: n.h + (f.h - n.h) * z };
}
/**
 * C8-7 터치 가로 화면 보스 상자: 폰 블라인드 P1 '보스가 화면 높이 25~30%로 작다'. 터치 가로 화면은 단추가 좌우 아래 모서리(조이스틱·스킬 호)와
 * 위 가운데 보스 막대뿐이라 보스 몸은 데스크톱 HUD 여백(v 0.54·top 0.5)보다 위아래로 더 써도 가리지 않는다(뿔·HUD 겹침은 raid-frame-shots가 잰다).
 * 좌우(h)는 그대로 — 오른쪽 스킬 호가 화면 30%를 덮는다. 영웅 몸은 바꾸지 않는다(아래 가운데 물약 띠).
 */
export const TOUCH_BOSS_BOX = { v: 0.7, top: 0.68 } as const;
/** C8-7 터치 가로 화면 보스전 거리 배율(안전 거리가 하한) */
export const TOUCH_BOSS_PULL = 0.75;
/**
 * C8-7 r3: 거리만 당기면 영웅 안전 상자가 하한이 돼 더 다가가지 못했다(카메라 시험: 하한 27.7m, 당긴 거리 24.4m).
 * 영웅·보스가 앞뒤로 10m 떨어져 위아래로 벌어지는 탓이라, 터치 가로 화면 보스전만 피치를 눕히고(-6°) 영웅 발 아래 여백을 조금 쓴다(v 0.64,
 * 영웅은 화면 가운데 아래 — 물약 띠는 그보다 오른쪽). 시험값: 보스 화면 높이 25% → 32%.
 */
export const TOUCH_BOSS_PITCH = -6;
export const TOUCH_HERO_V = 0.64;
export function touchBossBox(b: { v: number; top: number; h: number }): { v: number; top: number; h: number } {
  return { v: Math.max(b.v, TOUCH_BOSS_BOX.v), top: Math.max(b.top, TOUCH_BOSS_BOX.top), h: b.h };
}
/** 영웅·보스의 발~머리 경계가 HUD 여백 안에 들어가는 최소 거리. XZ 반경도 보수적으로 포함한다. */
function boundsDistance(dx: number, dz: number, height: number, radius: number, sy: number, cy: number, sp: number, cp: number, tanV: number, tanH: number, lookY: number, tanTop = tanV): number {
  const side = dx * cy - dz * sy, back = dx * sy + dz * cy;
  let dist = 0;
  for (let i = 0; i < 2; i++) {
    const y = (i === 0 ? 0 : height) - lookY;
    const up = y * cp - back * sp;
    const depth = -back * cp - y * sp;
    dist = Math.max(dist, Math.max((Math.abs(side) + radius) / tanH, (up + radius * sp) / tanTop, (-up + radius * sp) / tanV) - depth + radius * cp);
  }
  return dist;
}
/** 하위 객체 수(장면 루트 서명용, 할당 없음) */
function countDeep(o: THREE.Object3D): number {
  let n = o.children.length;
  for (let i = 0; i < o.children.length; i++) n += countDeep(o.children[i]);
  return n;
}
/** 연출·보정이 합쳐진 피치 범위(도) */
const PITCH_MIN = 14, PITCH_MAX = 44;
/** 카메라가 비켜 가야 하는 보스 몸 수평 반경(m): 몸 반경 3 + 여유 1 */
const BOSS_CLEAR = 4;
/** 다단 반동 간격 하한(ms). 라이라 연사·칼날 폭풍처럼 6~9틱마다 맞는 다단 타격이 105ms 반동을 이어 붙여 1초의 53~60%가 흔들렸다(feel-sim 2026-11-03).
 *  250ms면 연타 반동 비율이 약 0.4 이하로 내려가고, 기본 4연타(타격 간격 280ms 이상)는 매 타 반동을 유지한다. 더 센 반동(마무리·처치·피격)은 언제나 대체한다 */
const PUNCH_REPEAT_MS = 250;
/** 추적 정상 지연 상한(m). 지수 추적 지연 ≈ 속도/날카로움이라 날카로움 10에서 피의 돌진(약 16.7m/s)이 1.5m 뒤처져 영웅이 하단 HUD로 내려갔다.
 *  달리기(7m/s → 0.7m)는 그대로 두고 그보다 빠를 때만 조인다 */
const FOLLOW_MAX_LAG_M = 0.8;
/** 영웅 속도 평활 날카로움(1/s): 한 프레임 튐(보간 오차)으로 추적이 출렁이지 않게 */
const FOLLOW_SPEED_SMOOTH = 25;
/**
 * 카메라 충돌(2026-11-04 카메라 가림 레인). 하이엔드 ARPG의 두 갈래:
 *  - 관통: 상점·성문·큰 바위처럼 컷되지 않는 물체 안에 카메라가 들어가면 그 바깥면 앞까지 팔을 당긴다.
 *    당김은 빠르게(inRate) 하되 프레임당 maxInStep을 넘지 않고(달리기 추적 0.12m를 더해도 멀미 게이트 0.35m 안), 복귀는 느리게(outRate·maxOutStep) 한다.
 *    probe만큼 카메라 둘레를 키워 닿기 전에 미리 당기므로 이동 중 당김이 연속적이다.
 *  - 가림: 카메라는 밖인데 분수·가로등·기둥이 영웅 몸 시선을 둘 이상 막으면 그 인스턴스만 디더로 비친다(cut.ts 가림 칸).
 * 팔 길이 하한 minArm 아래로는 당기지 않는다(그 물체는 비침). 보스전에는 영웅 안전 거리(heroSafe) 아래로 당기지 않아 구도를 지킨다.
 */
export const CAMERA_COLLIDE = { probe: 0.9, minArm: 3.2, inRate: 16, maxInStep: 0.2, outRate: 2.6, maxOutStep: 0.12, fadeIn: 9, fadeOut: 4, hold: 0.25 } as const;
/** 충돌 대상 장면 루트 이름(맵 그레이박스·지역/마을/스테이지/필드 환경) */
const COLLIDE_ROOT = /^(map-|env-|town-env|stage-env|field-env)/;
/** 절대 줌 이벤트(핀치 등): detail = { zoom: 0..1, save?: boolean } */
export const CAMERA_SET_EVENT = 'dh-camera-set';
/** C8-6 터치 가로 화면 보스전 장면 비킴(화면 폭 비율, CameraRig.touchShift) */
export const TOUCH_BOSS_SHIFT = 0.05;

/**
 * 일반(레이드 밖) 보스 프레이밍 상자: 키(m)·몸 반경(m). motion-rig-anim #371 고목 수호자 키 약 9.4m(카엘 3.6배),
 * 발밑 접촉 그림자 원판 지름 최대 9m → 머리와 발밑 그림자를 360·390 세로에서도 함께 담는다(예전 7·3).
 */
export const FIELD_BOSS_FRAME = { h: 9.6, r: 4.5 } as const;

/** 세로 화면 거리 배율: 가로 1.3 미만부터 늘어나 상한 1.5(폰 세로 390x844 ≈ 1.5) */
export function portraitDistanceScale(aspect: number): number {
  return aspect < 1.3 ? Math.min(1.5, 1.3 / Math.max(0.1, aspect)) : 1;
}
/** 세로 화면 추가 피치(도): 가로 1.3에서 0 → 가로 0.5 이하에서 +9 */
export function portraitPitchDeg(aspect: number): number {
  return aspect >= 1.3 ? 0 : 9 * Math.min(1, (1.3 - aspect) / 0.8);
}

export class CameraRig {
  readonly camera: THREE.PerspectiveCamera;
  private readonly target = new THREE.Vector3();
  private readonly offset: THREE.Vector3;
  private viewH = 720;
  private viewW = 1280;
  /** C8-6 터치 가로 화면 보스전 구도 비킴(화면 폭 비율): 오른쪽 스킬 단추 호가 화면 오른쪽 약 30%를 덮어 비룡 머리가 단추 뒤로 갔다(블라인드 폰 E P1).
   * 보스 초점이 켜진 만큼(bossBlend) 투영 중심을 옮겨 장면을 왼쪽으로 민다. 0 = 끔(데스크톱·세로 화면) */
  touchShift = 0;
  /** 터치 가로 화면(장면 비킴이 켜진 화면)인가 */
  private touchLandscape(): boolean { return this.touchShift > 0 && this.camera.aspect >= 1.6; }
  private shiftNow = 0;
  private distanceScale = 1;
  /** 세로 화면 추가 피치(도) */
  private portraitPitch = 0;
  private nearDistanceScale = 1;
  private inited = false;
  // 펀치: 진폭(px)이 decayMs 동안 선형으로 0이 된다. 방향은 정규화한 월드 XZ.
  private punchPx = 0;
  private punchMs = 80;
  private punchT = 0;
  /** QA: 이번 프레임 타격 반동이 카메라를 민 거리(m)와 반동 세기(px, 흔들림 배율 적용 후). 이동·추적과 분리된 값 */
  readonly punchQa = { m: 0, px: 0 };
  private punchDx = 0;
  private punchDz = 1;
  /** 직전 반동이 시작된 뒤 지난 시간(ms)과 그 반동의 시작 세기(px). 다단 반동 간격 하한(PUNCH_REPEAT_MS)에 쓴다 */
  private punchSince = 1e9;
  private punchStartAmp = 0;
  /** 영웅 수평 속도(m/s, 렌더 보간 위치 기준·평활). 빠른 이동에서 추적을 조인다(FOLLOW_MAX_LAG_M) */
  private heroSpeed = 0;
  private lastHx = 0;
  private lastHz = 0;
  private hitTick = -1;
  private hitStrength = 0;
  /** QA 디버그: 거리 배율과 바라보는 높이(m). 게임 화면에서는 1, 0 */
  private debugZoom = 1;
  private debugLookY = 0;
  /** QA 구도 실험: 피치(도)·거리(m) 덮어쓰기. 0이면 CAMERA 값 */
  private debugPitch = 0;
  private debugDist = 0;
  /** 보스전 구도: 보스가 활성인 동안 카메라를 뒤로 빼고 시선을 영웅과 보스 사이로 옮긴다(부드럽게 전환) */
  private bossOn = false;
  private bossX = 0;
  private bossZ = 0;
  private bossBlend = 0;
  /** 보스 등장 직후 소개 구도 유지 시간(초)과, 그 뒤 플레이어 줌이 풀리는 정도(0 = 소개 구도, 1 = 플레이어 줌 그대로) */
  private introHold = 0;
  private bossRel = 1;
  /** 보정이 고른 쪽: -1 = 보스 앞(영웅 쪽), 1 = 보스 뒤, 0 = 보정 없음. 보스가 통로를 벗어날 때까지 유지한다 */
  private guardSide = 0;
  /** 보스전 근접 정도 0..1(플레이어가 줌을 최소 쪽으로 당겼고 소개 구도가 풀린 정도). 보스 방향 화살표가 읽는다 */
  bossClose = 0;
  /** 플레이어 줌: 0 = 콘셉트 전투 구도, 1 = 광역 구도. zoom은 목표, zoomNow는 부드럽게 따라간다 */
  private zoom = loadCameraPrefs(CAMERA_ZOOM.initial).zoom;
  private zoomNow = this.zoom;
  private userYaw = loadCameraPrefs(CAMERA_ZOOM.initial).yaw;
  /** 사용자 카메라 높이 보정(도)과 타격 흔들림 배율 */
  private tilt = 0;
  private shakeScale = SHAKE_SCALE[loadCameraPrefs(CAMERA_ZOOM.initial).shake];
  /** 전투 연출 컷 */
  readonly cine = new CineDirector();
  /** URL로 고정한 연출 강도(?cine=off|low|normal|strong). 렌더 픽스처(fixture.html)는 기준 장면 구도를 지키려고 기본 끔 */
  private cineOverride: number | null = null;
  /** QA 구도 실험(?qa&cam=피치,거리,화각): 0이면 CAMERA 값 */
  private qaFov = 0;
  /** QA 앞보기 덮어쓰기(m). 음수면 CAMERA 값 */
  private qaLook = -1;
  /** 카메라 충돌·가림 판정(장면 키트 인스턴스 상자). attachScene 전에는 꺼져 있다 */
  readonly collider = new CameraCollider();
  private collideScene: THREE.Object3D | null = null;
  private collideSig = -1;
  /** 실제로 쓴 팔 길이(당김 상태). NaN이면 당기지 않은 상태 */
  private armNow = Number.NaN;
  /** 지난 프레임에 쓴 팔 길이(첫 접촉 당김의 출발점). NaN = 순간이동 직후 */
  private armPrev = Number.NaN;
  /** 마지막 보스 구도 영웅 안전 거리(보스전 당김 하한) */
  private heroSafeLast = 0;
  /** 가림 칸 상태: 중심 x, z, 비침 정도, 마지막으로 가린 뒤 지난 시간(s) */
  private readonly occSlots = new Float32Array(OCCLUDER_SLOTS * 4).fill(0);
  /** QA 읽기: 이번 프레임 당김(m)·가림 칸 수·관통 미해결 수 */
  readonly collideQa = { pull: 0, occ: 0, unresolved: 0, boxes: 0 };
  /** 카메라 충돌을 끄는 QA 스위치(?camcol=0) */
  collideEnabled = true;

  constructor() {
    // far는 안개 끝(맵 fogFar 108~115m)보다 조금 멀리: 낮은 피치에서 지평선 쪽 원경을 끝없이 그리지 않는다
    this.camera = new THREE.PerspectiveCamera(CAMERA.fovDeg, 1, 0.5, 135);
    const pitch = (CAMERA.pitchDeg * Math.PI) / 180;
    this.offset = new THREE.Vector3(0, Math.sin(pitch) * CAMERA.distance, Math.cos(pitch) * CAMERA.distance);
    if (typeof window !== 'undefined') {
      const cineParam = new URLSearchParams(location.search).get('cine');
      if (cineParam && cineParam in CINE_SCALE) this.cineOverride = CINE_SCALE[cineParam as keyof typeof CINE_SCALE];
      else if (/fixture\.html$/.test(location.pathname)) this.cineOverride = 0;
      this.applyPrefs(loadCameraPrefs(CAMERA_ZOOM.initial), false);
      window.addEventListener(CAMERA_PREFS_EVENT, (e) => this.applyPrefs((e as CustomEvent<CameraPrefs>).detail, true));
      window.addEventListener('dh-camera-yaw', (e) => {
        const d = (e as CustomEvent<{ delta?: number; reset?: boolean; save?: boolean }>).detail;
        if (!d) return;
        if (d.reset) {
          this.userYaw = 0;
          this.setZoomLive(CAMERA_ZOOM.initial);
          this.zoomNow = this.zoom;
          this.cine.reset();
          this.inited = false;
        } else if (typeof d.delta === 'number' && Number.isFinite(d.delta)) {
          this.userYaw = wrapCameraYaw(this.userYaw + d.delta);
        }
        if (d.reset || d.save) updateCameraPrefs({ yaw: this.userYaw, zoom: this.zoom });
      });
      window.addEventListener(CAMERA_SET_EVENT, (e) => {
        const d = (e as CustomEvent<{ zoom: number; save?: boolean }>).detail;
        if (!d || !Number.isFinite(d.zoom)) return;
        if (d.save) this.setZoom(d.zoom);
        else this.setZoomLive(d.zoom);
      });
      const params = new URLSearchParams(location.search);
      if (params.has('qa') && params.get('camcol') === '0') this.collideEnabled = false;
      const cam = params.has('qa') ? params.get('cam') : null;
      if (cam) {
        const [p, d, f, la] = cam.split(',').map(Number);
        this.debugPitch = p || 0; this.debugDist = d || 0; this.qaFov = f || 0; this.qaLook = Number.isFinite(la) ? la : -1;
        if (this.qaFov) this.camera.fov = this.qaFov;
      }
      // 마우스 휠 줌(PC). 게임 화면 위에서만, UI 패널 스크롤은 건드리지 않는다
      window.addEventListener('wheel', (e) => {
        const el = e.target as HTMLElement | null;
        if (el && el.tagName !== 'CANVAS') return;
        this.setZoom(this.zoom + Math.sign(e.deltaY) * CAMERA_ZOOM.wheelStep);
      }, { passive: true });
      // 키보드 줌(+/-)과 UI 버튼(window 이벤트 'dh-camera-zoom', detail = 변화량. 0이면 가까이↔멀리 전환)
      window.addEventListener('keydown', (e) => {
        if (e.repeat) return;
        if (e.code === 'Equal' || e.code === 'NumpadAdd') this.setZoom(this.zoom - CAMERA_ZOOM.wheelStep);
        else if (e.code === 'Minus' || e.code === 'NumpadSubtract') this.setZoom(this.zoom + CAMERA_ZOOM.wheelStep);
      });
      window.addEventListener('dh-camera-zoom', (e) => {
        const d = Number((e as CustomEvent).detail) || 0;
        // 버튼(d = 0): 넓게/중간 → 기본(0) → 근접(-1) → 넓게(1) 순서로 한 단계씩(0.25 같은 중간 값도 0을 건너뛰지 않는다)
        this.setZoom(d === 0 ? (this.zoom > 0.05 ? 0 : this.zoom > CAMERA_ZOOM.min + 0.05 ? CAMERA_ZOOM.min : 1) : this.zoom + d);
      });
    }
  }

  /** 설정 화면의 카메라 설정을 적용한다(줌은 바뀌었을 때만) */
  private applyPrefs(p: CameraPrefs, fromEvent: boolean): void {
    if (!p) return;
    this.userYaw = wrapCameraYaw(p.yaw);
    this.tilt = p.tilt;
    this.shakeScale = SHAKE_SCALE[p.shake];
    if (this.shakeScale === 0) this.punchPx = 0;
    this.cine.intensity = this.cineOverride ?? CINE_SCALE[p.cine];
    if (fromEvent && Math.abs(p.zoom - this.zoom) > 1e-3) this.setZoomLive(p.zoom);
  }

  /** 저장하지 않고 줌 목표만 바꾼다(핀치 중). UI 표시는 맞춘다 */
  private setZoomLive(z: number): void {
    this.zoom = clampZoom(z);
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('dh-camera-zoomed', { detail: this.zoom }));
  }

  /** 줌 목표(0..1)를 바꾸고 저장한다. UI가 표시를 맞추도록 'dh-camera-zoomed'(detail = 0..1)를 알린다 */
  setZoom(z: number): void {
    this.setZoomLive(z);
    updateCameraPrefs({ zoom: this.zoom, yaw: this.userYaw });
  }

  get zoomLevel(): number { return this.zoom; }
  get yaw(): number { return this.userYaw; }

  /** 전투 연출 컷을 건다. x,z는 초점 지점(보스 등). 끔이거나 더 중요한 컷이 진행 중이면 false */
  cue(id: ShotId, x = 0, z = 0): boolean {
    return this.cine.play(SHOTS[id], x, z);
  }

  resize(w: number, h: number): void {
    this.viewH = Math.max(1, h);
    this.viewW = Math.max(1, w);
    this.shiftNow = -1; // 다음 update에서 화면 비킴을 새 크기로 다시 건다
    const aspect = w / Math.max(1, h);
    this.camera.aspect = aspect;
    // 세로 화면에서는 가로 시야가 모자라므로 멀어진다. 단 너무 멀면(예전 상한 1.9) 카메라가 광장 밖 담·수풀 뒤로 빠져
    // 화면 아래 20~40%가 가까운 바닥·가림 물체로 채워졌다(vfx-look-art 감사 2026-11-03 village-m390, 블라인드 P0).
    // 그래서 거리는 덜 멀어지게 하고(상한 1.5), 대신 내려보는 각을 올려(portraitPitch) 가로 시야를 바닥 쪽에서 되찾는다.
    this.distanceScale = portraitDistanceScale(aspect);
    // 근접 숄더뷰는 예전 배율(상한 1.9)을 쓴다: 가까운 구도에서 영웅 몸 전체 안전 여백이 이 값으로 맞춰져 있다
    this.nearDistanceScale = aspect < 1.3 ? Math.min(1.9, 1.3 / aspect) : 1;
    this.portraitPitch = portraitPitchDeg(aspect);
    this.camera.updateProjectionMatrix();
  }

  /** 다음 update에서 추적 목표를 즉시 맞춘다(맵 전환 직후). */
  snap(): void {
    this.inited = false;
    this.bossBlend = 0;
    this.guardSide = 0;
    this.punchPx = 0; this.hitTick = -1; this.hitStrength = 0;
    this.punchSince = 1e9; this.punchStartAmp = 0; this.heroSpeed = 0;
    this.cine.reset();
  }

  /** QA 디버그 전용(고정 카메라 규칙은 그대로, 거리만 줄여 근접 확인) */
  setDebugZoom(zoom: number, lookY: number): void {
    this.debugZoom = zoom > 0 ? zoom : 1;
    this.debugLookY = lookY;
  }

  /** QA 디버그 전용: 피치·거리 실험(0이면 원래 값) */
  setDebugPose(pitchDeg: number, distance: number): void {
    this.debugPitch = pitchDeg;
    this.debugDist = distance;
    this.inited = false;
  }

  /** 매 프레임 보스 상태를 넘긴다. */
  /** 보스 프레이밍 크기(높이·반경 m). 기본 7·3, 레이드 비룡처럼 큰 보스는 render가 키운다(L11: 머리가 화면 위로 잘렸다) */
  private bossH = 7;
  private bossR = 3;
  /** 보스 전투 구도 하한 거리(m)·피치 보정(도). 레이드는 보스가 커서(키 9.9m) 기본 28m 하한이면 화면 높이의 15%로 작아진다 */
  private bossMinDist: number = BOSS_FRAME.distance;
  private bossPitch = 0;
  /** 보스 전투 시선 높이 보정(m): 큰 보스는 바닥 대신 몸 가운데를 겨눠야 위아래 여백이 고르게 쓰여 거리가 줄어든다 */
  private bossLookY = 0; // 피치·시선 보정은 bossRel(등장 소개 구도 동안 0)을 곱해 인트로 컷을 바꾸지 않는다(C8-5 블라인드 D −0.5)
  setBossSize(h: number, r: number, minDist: number = BOSS_FRAME.distance, pitch = 0, lookY = 0): void { this.bossH = h; this.bossR = r; this.bossMinDist = minDist; this.bossPitch = pitch; this.bossLookY = lookY; }
  setBossFocus(active: boolean, bx: number, bz: number): void {
    // 등장 순간: 짧게 소개 구도(둘 다 화면에 담는 넓은 구도)를 먼저 보여 주고, 그 뒤 플레이어 줌이 가까운 시점으로 풀린다
    if (active && !this.bossOn) { this.introHold = CAMERA_ZOOM.bossIntroHold; this.bossRel = 0; }
    this.bossOn = active;
    this.bossX = bx;
    this.bossZ = bz;
  }

  /** 유효 피해 결과만 반동을 만든다. 한 틱의 광역 적중·처치는 가장 강한 하나로 합친다. */
  onCombatEvent(e: GameEvent, reduceFlash = false): void {
    // 환경 화상·분출구 처치는 무기 충격이 아니다. 틱 합산 상태도 건드리지 않는다.
    if ((e.flags & HitFlag.Environment) !== 0) return;
    let strength = 0, direction = e.d;
    if (e.type === Ev.Hit && e.a > 0) {
      const f = e.flags;
      strength = e.b === 2 ? 5.2 : f & 32 ? 1.2 : f & 16 ? 1.6 : f & 2 ? 4.2 : f & 4 ? 3.5 : f & 1 ? 3.2 : 2.6;
    } else if (e.type === Ev.Kill) { strength = 4.4; direction = e.c; }
    else return;
    if (reduceFlash) strength *= 0.35;
    if (e.tick !== this.hitTick) { this.hitTick = e.tick; this.hitStrength = 0; }
    if (strength <= this.hitStrength) return;
    this.hitStrength = strength;
    this.punch(strength, e.type === Ev.Kill || (e.flags & 2) !== 0 ? 125 : 105, Math.sin(direction), Math.cos(direction));
  }

  /** CameraPunch 이벤트: px는 CAMERA_PUNCH.maxPx로 제한한다. 이미 진행 중이면 더 큰 쪽을 유지한다. */
  punch(px: number, decayMs: number, dx: number, dz: number): void {
    const amp = Math.min(CAMERA_PUNCH.maxPx, Math.max(0, px)) * this.shakeScale;
    if (amp <= 0) return;
    const remaining = this.punchPx * Math.pow(Math.max(0, 1 - this.punchT / this.punchMs), 2);
    if (amp < remaining) return;
    // 다단 반동 간격 하한: 직전 반동 시작 뒤 PUNCH_REPEAT_MS 안에는 더 센 반동만 새로 건다(같거나 약한 연타가 이어 붙어 지속 흔들림이 되지 않게)
    if (this.punchSince < PUNCH_REPEAT_MS && amp <= this.punchStartAmp) return;
    this.punchSince = 0;
    this.punchStartAmp = amp;
    const len = Math.hypot(dx, dz);
    this.punchDx = len > 1e-4 ? dx / len : 0;
    this.punchDz = len > 1e-4 ? dz / len : 1;
    this.punchPx = amp;
    this.punchMs = Math.max(16, decayMs || CAMERA_PUNCH.decayMs);
    this.punchT = 0;
  }

  /** 영웅 위치(보간 후)를 따라간다. 매 프레임 호출. */
  update(hx: number, hz: number, frameDt: number, frozen = false): void {
    const t = this.target;
    this.punchSince += frameDt * 1000;
    // 영웅 속도: 순간이동(맵 전환·14m 이상)은 속도로 치지 않는다
    if (frameDt > 0 && this.inited) {
      const step = Math.hypot(hx - this.lastHx, hz - this.lastHz);
      const inst = step > 14 ? 0 : step / frameDt;
      this.heroSpeed += (inst - this.heroSpeed) * damp(FOLLOW_SPEED_SMOOTH, frameDt);
    }
    this.lastHx = hx; this.lastHz = hz;
    const cp = this.cine.update(frameDt);
    const yaw = this.userYaw + (cp.yaw * Math.PI) / 180;
    const sy = Math.sin(yaw), cy = Math.cos(yaw);
    // 짧은 연출 궤도는 조작 축에 넣지 않는다. 이동·자동 조준은 월드 축으로 한 번만 변환된다.
    cameraYaw.rad = this.userYaw;
    this.bossBlend += ((this.bossOn ? 1 : 0) - this.bossBlend) * damp(2.2, frameDt);
    const bb = this.bossBlend;
    this.zoomNow += (this.zoom - this.zoomNow) * damp(CAMERA_ZOOM.sharpness, frameDt);
    const zn = this.zoomNow;
    // 줌 0 아래 = 숄더뷰 근접(0..1). 보스전에서는 소개 구도가 끝난 뒤에만 보스 프레이밍을 풀어 준다(rel = 1이면 예전 보스 구도 그대로)
    const nearT = zn < 0 ? Math.min(1, zn / CAMERA_ZOOM.min) : 0;
    if (this.bossOn) {
      if (this.introHold > 0) this.introHold = Math.max(0, this.introHold - frameDt);
      this.bossRel += ((this.introHold > 0 ? 0 : 1) - this.bossRel) * damp(1.6, frameDt);
    }
    const nearB = nearT * this.bossRel;
    const rel = 1 - nearB;
    this.bossClose = nearB;
    // 시선 이동은 최대 maxShift(m): 보스방 입구에서 멀리 있을 때도 영웅이 화면 아래로 잘리지 않는다
    const bdx = this.bossX - hx, bdz = this.bossZ - hz;
    // 앞뒤 초점은 화면 아래 영웅을 보호하도록 제한하고, 옆으로 벌어지면 둘의 중간을 잡는다.
    // 근접 숄더뷰에서는 시선을 보스 쪽으로 거의 끌지 않는다(끌면 영웅이 카메라 밑으로 들어간다). 보스는 카메라를 돌려 맞춘다
    const shiftW = bb * rel;
    const sideShift = (bdx * cy - bdz * sy) * 0.5 * shiftW;
    const backShift = Math.max(-BOSS_FRAME.maxShift, Math.min(BOSS_FRAME.maxShift, (bdx * sy + bdz * cy) * BOSS_FRAME.toward)) * shiftW;
    // 연출 초점(보스 등) 쪽으로 시선을 옮긴다
    const fw = cp.focus;
    let gx = hx + cy * sideShift + sy * backShift;
    let gz0 = hz - sy * sideShift + cy * backShift;
    if (fw > 1e-4) { gx += (cp.fx - gx) * fw; gz0 += (cp.fz - gz0) * fw; }
    const look = this.qaLook >= 0 ? this.qaLook
      : zn >= 0 ? CAMERA.lookAhead + (CAMERA_ZOOM.farLookAhead - CAMERA.lookAhead) * zn
      : CAMERA.lookAhead + (CAMERA_ZOOM.nearLookAhead - CAMERA.lookAhead) * nearT;
    // 궤도(요): 카메라가 영웅 둘레를 돌면 앞보기 방향도 같이 돈다
    gx -= sy * look * this.debugZoom;
    let gz = gz0 - cy * look * this.debugZoom;
    // 숄더뷰: 시선을 카메라 오른쪽으로 비켜 영웅이 화면 왼쪽에 서고, 정면의 적·보스가 영웅 몸에 가리지 않는다
    if (nearT > 0) { const side = CAMERA_ZOOM.nearSide * nearT / this.nearDistanceScale; gx += cy * side; gz -= sy * side; }
    if (!this.inited || Math.abs(gx - t.x) > 14 || Math.abs(gz - t.z) > 14) {
      t.set(gx, 0, gz);
      this.inited = true;
      this.armNow = Number.NaN; this.armPrev = Number.NaN; // 순간이동·맵 전환: 당김 상태를 이어 가지 않는다
      // 가림 칸도 비운다(새 맵·재시작에서 같은 좌표의 다른 물체가 잔상으로 비치지 않게)
      this.occSlots.fill(0);
      for (let j = 0; j < OCCLUDER_SLOTS; j++) { this.occSlots[j * 4 + 3] = 1e9; setOccluderFade(j, 0, 0, 0); }
    } else if (!frozen) {
      // 히트스톱(frozen) 동안은 추적을 멈춰 정지 순간 화면이 따라 흐르지 않게 한다. 반동·연출 컷은 그대로.
      // 빠른 이동(피의 돌진·회피)에서는 지수 추적의 정상 지연(속도/날카로움)이 FOLLOW_MAX_LAG_M을 넘지 않게 날카로움을 올린다
      const k = damp(Math.max(CAMERA.followSharpness, this.heroSpeed / FOLLOW_MAX_LAG_M), frameDt);
      t.x += (gx - t.x) * k;
      t.z += (gz - t.z) * k;
    }
    const base = this.debugDist || (zn >= 0
      ? CAMERA.distance + (CAMERA_ZOOM.farDistance - CAMERA.distance) * zn
      : CAMERA.distance + (CAMERA_ZOOM.nearDistance - CAMERA.distance) * nearT);
    const pitchDeg = Math.min(PITCH_MAX, Math.max(PITCH_MIN, (this.debugPitch || CAMERA.pitchDeg) + this.portraitPitch * (1 - nearT) + this.tilt + cp.pitch + CAMERA_ZOOM.nearPitch * nearT + this.bossPitch * this.bossBlend * this.bossRel * rel + (this.touchLandscape() ? TOUCH_BOSS_PITCH * this.bossBlend * rel : 0)));
    const pitch = (pitchDeg * Math.PI) / 180;
    const fov = (this.qaFov || CAMERA.fovDeg) + cp.fov;
    const ly = this.debugLookY + cp.lookY + CAMERA_ZOOM.nearLookY * nearT + this.bossLookY * this.bossBlend * this.bossRel * rel;
    const sp = Math.sin(pitch), cosPitch = Math.cos(pitch);
    const tanV = Math.tan(fov * Math.PI / 360) * 0.7, tanH = tanV * this.camera.aspect;
    const fit = Math.max(this.bossMinDist,
      boundsDistance(hx - t.x, hz - t.z, 2.2, 0.7, sy, cy, sp, cosPitch, tanV, tanH, ly),
      boundsDistance(this.bossX - t.x, this.bossZ - t.z, this.bossH, this.bossR, sy, cy, sp, cosPitch, tanV, tanH, ly)) * rel;
    // fit은 이미 aspect를 포함하므로 세로 화면 거리 배율을 다시 곱하지 않는다.
    const scaledBase = base * (this.distanceScale + (this.nearDistanceScale - this.distanceScale) * nearT);
    let dist = (scaledBase + (Math.max(scaledBase, fit) - scaledBase) * bb) * this.debugZoom * cp.dist;
    if (this.bossOn || bb > 0.001) {
      // 안전 거리는 연출/보스 전환을 전부 적용한 뒤의 물리 거리 하한이다.
      // fit까지 bb·cp.dist로 줄이면 첫 접근과 인트로에서 영웅이 HUD 아래로 잘린다.
      // 720px 기준 상단 180px·하단 554px 안쪽. 카엘 1.75m × 1.5배와 작은 동작 여유를 포함한다.
      const tan = Math.tan(fov * Math.PI / 360);
      const box = bossBox(zn);
      // 터치 가로 화면(장면 비킴이 켜진 화면)만 보스 상자를 넓힌다 — 세로 화면·데스크톱은 그대로
      const touchL = this.touchLandscape();
      const bbox = touchL ? touchBossBox(box) : box;
      // 근접(rel < 1)에서는 영웅 몸만 안전 상자 안에 두고, 보스는 플레이어가 고른 거리를 따른다(화면 밖이면 방향 화살표)
      const heroSafe = this.heroSafeLast = boundsDistance(hx - t.x, hz - t.z, 2.7, 0.8, sy, cy, sp, cosPitch, tan * (touchL ? Math.max(box.v, TOUCH_HERO_V) : box.v), tan * box.h * this.camera.aspect, ly, tan * box.top);
      // 보스가 꺼질 때 보스 쪽 안전 거리가 한 프레임에 사라져 카메라가 튀지 않게, 꺼진 뒤에는 bb(지수 감쇠)를 따라 부드럽게 줄인다(영웅 쪽은 그대로)
      const safe = Math.max(heroSafe,
        boundsDistance(this.bossX - t.x, this.bossZ - t.z, this.bossH, this.bossR, sy, cy, sp, cosPitch, tan * bbox.v, tan * bbox.h * this.camera.aspect, ly, tan * bbox.top) * rel * (this.bossOn ? 1 : bb));
      // 영웅 쪽 안전 거리도 꼬리(bb < 0.05)에서는 같은 식으로 줄여, bb가 0.001에 닿아 블록이 꺼지는 순간 거리가 계단으로 튀지 않게 한다
      // C8-7 터치 가로 화면: 기본 거리가 하한을 정해 보스가 화면 높이 ~27%에 머물렀다(1차 상자만 넓힌 회차에서 카메라 좌표 불변 실측).
      // 보스 초점만큼 거리를 당기고, 아래 안전 거리(영웅 상자·넓힌 보스 상자)가 하한으로 남는다
      if (touchL) dist *= 1 - (1 - TOUCH_BOSS_PULL) * bb * rel;
      dist += (Math.max(dist, safe) - dist) * (this.bossOn ? 1 : Math.min(1, bb * 20));
      // 근접에서 보스가 영웅~카메라 통로(카메라 쪽)에 들어오면 카메라가 보스 몸 안으로 들어간다. 카메라의 수평 위치가
      // 보스 원(반경 BOSS_CLEAR) 밖에 놓이게 거리를 바꾼다: 가능하면 보스 앞(영웅 쪽)으로 당기고, 영웅 안전 거리보다 짧아지면 보스 뒤로 민다.
      // 고른 쪽(guardSide)은 보스가 통로를 벗어날 때(|side| >= BOSS_CLEAR 또는 거리가 (lo, hi) 밖)까지 유지한다: 매 프레임 고르면
      // 보스가 앞뒤로 움직일 때 쪽이 뒤집혀 보간 경로가 보스 몸을 지났다. 같은 쪽 목표는 보스가 움직이는 만큼 연속이라 보간 없이 따라가고,
      // 쪽이 바뀌어야 할 때(영웅 안전 거리 아래로 당길 수 없음)는 보간 없이 한 번에 자른다.
      let guard = 0, pick = 0;
      if (nearB > 0.001) {
        const bdx2 = this.bossX - t.x, bdz2 = this.bossZ - t.z;
        const back = bdx2 * sy + bdz2 * cy, side = bdx2 * cy - bdz2 * sy;
        if (Math.abs(side) < BOSS_CLEAR) {
          const w = Math.sqrt(BOSS_CLEAR * BOSS_CLEAR - side * side);
          const lo = (back - w) / cosPitch, hi = (back + w) / cosPitch;
          if (dist > lo && dist < hi) {
            pick = this.guardSide;
            if (pick <= 0 && lo < heroSafe) pick = 1;
            else if (pick === 0) pick = -1;
            guard = (pick < 0 ? lo : hi) - dist;
          }
        }
      }
      this.guardSide = pick;
      dist += guard;
    } else { this.guardSide = 0; this.heroSafeLast = 0; }
    dist = this.collide(dist, pitch, sy, cy, ly, hx, hz, frameDt, bb);
    const horiz = Math.cos(pitch) * dist;
    this.offset.set(sy * horiz, Math.sin(pitch) * dist, cy * horiz);
    // 영웅과 카메라 사이의 벽·지붕·소품을 모두 잘라 낼 수 있게 컷 범위를 카메라 수평 거리에 맞춘다
    setCutawayRange(horiz - look + 2.5);
    setCutawayDirection(-sy, -cy);
    setCutawayBoss(this.bossX, this.bossZ, this.bossOn ? Math.max(bb, 0.5) : bb, t.x + sy * horiz, t.z + cy * horiz);
    // 좁은 화면의 첫 보스 접근은 안전 프레이밍 거리가 기본 far(135m)를 넘을 수 있다.
    // 화면 안에 담은 캐릭터가 깊이 클리핑으로 사라지지 않도록 그 경계까지만 여유를 준다.
    const far = this.bossOn || bb > 0.001
      ? Math.max(135, dist + Math.max(Math.hypot(hx - t.x, hz - t.z), Math.hypot(this.bossX - t.x, this.bossZ - t.z)) + 10)
      : 135;
    // 가로 화면(가로/세로 1.6 이상)에서만 비킨다: 세로 화면은 단추가 아래에 있다
    const shift = this.camera.aspect >= 1.6 ? this.touchShift * bb : 0;
    if (Math.abs(shift - this.shiftNow) > 1e-4) {
      this.shiftNow = shift;
      if (shift > 1e-4) this.camera.setViewOffset(this.viewW, this.viewH, shift * this.viewW, 0, this.viewW, this.viewH);
      else this.camera.clearViewOffset();
    }
    if (Math.abs(this.camera.fov - fov) > 1e-3 || Math.abs(this.camera.far - far) > 1e-3) {
      this.camera.fov = fov;
      this.camera.far = far;
      this.camera.updateProjectionMatrix();
    }

    let ox = 0, oz = 0;
    if (this.punchPx > 0) {
      this.punchT += frameDt * 1000;
      const left = 1 - this.punchT / this.punchMs;
      if (left <= 0) {
        this.punchPx = 0;
      } else {
        const worldPerPx = (2 * Math.tan((this.camera.fov * Math.PI) / 360) * dist) / this.viewH;
        // 한 번 밀리고 작게 되돌아오는 감쇠 반동. 난수·반복 진동·줌 펄스 없음.
        const recoil = left * left * Math.cos((1 - left) * Math.PI * 1.25);
        const m = this.punchPx * recoil * worldPerPx;
        ox = this.punchDx * m;
        oz = this.punchDz * m;
      }
    }
    this.punchQa.m = Math.hypot(ox, oz);
    this.punchQa.px = this.punchPx;
    const cam = this.camera;
    cam.position.set(t.x + ox + this.offset.x, this.offset.y + ly, t.z + oz + this.offset.z);
    cam.lookAt(t.x + ox, ly, t.z + oz);
  }

  /** 충돌 대상 장면(키트 인스턴스가 붙는 루트들의 부모)을 연결한다 */
  attachScene(scene: THREE.Object3D): void { this.collideScene = scene; this.collideSig = -1; }

  /** 장면 루트 구성이 바뀌었으면(맵 로드·환경 준비·필드 청크) 충돌 대상을 다시 모은다. 할당은 바뀔 때만 */
  private syncCollider(): void {
    const sc = this.collideScene;
    if (!sc) return;
    let sig = 0;
    const kids = sc.children;
    for (let i = 0; i < kids.length; i++) {
      const r = kids[i];
      if (!COLLIDE_ROOT.test(r.name)) continue;
      sig = (sig * 31 + r.id * 7 + countDeep(r) + (r.visible ? 1 : 0)) % 1000000007;
    }
    if (sig === this.collideSig) return;
    this.collideSig = sig;
    const roots: THREE.Object3D[] = [];
    for (let i = 0; i < kids.length; i++) if (COLLIDE_ROOT.test(kids[i].name)) roots.push(kids[i]);
    this.collider.setRoots(roots);
  }

  /**
   * 카메라 충돌: 원하는 팔 길이 dist에서 관통이면 당긴 길이를, 아니면 (부드럽게 복귀하며) dist를 돌려준다. 가림 칸도 갱신한다.
   * 연출 컷 중에도 같은 규칙이다(컷 카메라가 벽 안에 들어가는 것도 관통이다).
   */
  private collide(dist: number, pitch: number, sy: number, cy: number, ly: number, hx: number, hz: number, frameDt: number, bb: number): number {
    const C = CAMERA_COLLIDE, q = this.collideQa;
    q.pull = 0; q.occ = 0; q.unresolved = 0;
    if (!this.collideEnabled || !this.collideScene) { this.armNow = Number.NaN; this.armPrev = Number.NaN; this.fadeSlots(frameDt, 0); return dist; }
    this.syncCollider();
    const t = this.target, col = this.collider;
    col.gather(hx, hz);
    q.boxes = col.boxN;
    const cp = Math.cos(pitch), ux = sy * cp, uy = Math.sin(pitch), uz = cy * cp;
    const bossy = this.bossOn || bb > 0.01;
    const minArm = Math.max(C.minArm, bossy ? this.heroSafeLast * bb : 0);
    const r = col.solve(t.x, ly, t.z, ux, uy, uz, dist, hx, 0, hz, Math.min(minArm, dist), C.probe);
    const want = Math.min(dist, r.maxArm);
    // 당김이 없던 상태에서 새 관통을 만나도 지난 프레임 팔 길이에서 출발해 프레임당 maxInStep으로 당긴다(첫 접촉 순간 이동 방지)
    const from = Number.isNaN(this.armNow) ? this.armPrev : this.armNow;
    // 프레임당 상한은 60fps 기준이다. 30fps 기기에서도 초당 당김·복귀 속도가 같도록 dt에 비례시킨다(최대 2배)
    const fs = Math.min(2, frameDt * 60), inStep = C.maxInStep * fs, outStep = C.maxOutStep * fs;
    let arm: number;
    if (Number.isNaN(from) || frameDt <= 0) arm = want;
    else if (Number.isNaN(this.armNow) && want >= dist) arm = want;
    else if (want < from) arm = Math.max(want, from - Math.min(inStep, (from - want) * damp(C.inRate, frameDt)));
    else if (Number.isNaN(this.armNow)) arm = want;
    else if (want < this.armNow) arm = Math.max(want, this.armNow - Math.min(inStep, (this.armNow - want) * damp(C.inRate, frameDt)));
    else arm = Math.min(want, this.armNow + Math.min(outStep, (want - this.armNow) * damp(C.outRate, frameDt)));
    // 보스전이 켜질 때 이미 당겨져 있으면 느린 복귀(outStep) 대신 당김 속도(inStep)로 보스 구도 하한까지 올린다
    if (bossy && !Number.isNaN(from) && frameDt > 0) {
      const floor = Math.min(minArm, dist);
      if (arm < floor) arm = Math.min(floor, Math.max(arm, from + inStep));
    }
    // 당김이 끝났으면(원하는 길이에 닿음) 상태를 풀어 줌·보스 구도 변화가 제한 없이 따라가게 한다
    this.armNow = arm >= dist - 1e-3 ? Number.NaN : arm;
    this.armPrev = arm;
    q.pull = dist - arm; q.unresolved = r.unresolved;
    // 가림 칸: 새 가림체는 칸을 잡고 정도를 올리며, 사라진 가림체는 hold 뒤 내린다
    const S = this.occSlots;
    for (let j = 0; j < OCCLUDER_SLOTS; j++) S[j * 4 + 3] += frameDt;
    for (let k = 0; k < r.occN && k < OCC_MAX; k++) {
      const x = r.occ[k * 3], z = r.occ[k * 3 + 1];
      let slot = -1, free = -1, oldest = 0;
      for (let j = 0; j < OCCLUDER_SLOTS; j++) {
        if (S[j * 4 + 2] > 0 && Math.abs(S[j * 4] - x) < 0.05 && Math.abs(S[j * 4 + 1] - z) < 0.05) { slot = j; break; }
        if (free < 0 && S[j * 4 + 2] <= 0) free = j;
        if (S[j * 4 + 3] > S[oldest * 4 + 3]) oldest = j;
      }
      // 새 칸은 아주 작은 정도로 점유 표시해 같은 프레임의 다음 가림체가 같은 칸을 덮어쓰지 않게 한다
      if (slot < 0) { slot = free >= 0 ? free : oldest; S[slot * 4] = x; S[slot * 4 + 1] = z; S[slot * 4 + 2] = 1e-4; }
      S[slot * 4 + 3] = 0;
    }
    this.fadeSlots(frameDt, 1);
    return arm;
  }

  /** 가림 칸 정도를 올리고 내려 셰이더에 넘긴다(on = 0이면 모두 내린다) */
  private fadeSlots(frameDt: number, on: number): void {
    const C = CAMERA_COLLIDE, S = this.occSlots, q = this.collideQa;
    for (let j = 0; j < OCCLUDER_SLOTS; j++) {
      const b = j * 4;
      const active = on > 0 && S[b + 3] <= C.hold && (S[b + 2] > 0 || S[b + 3] === 0);
      const goal = active ? 1 : 0;
      const a = S[b + 2] + (goal - S[b + 2]) * damp(goal > S[b + 2] ? C.fadeIn : C.fadeOut, Math.max(0, frameDt));
      S[b + 2] = a < 0.01 && goal === 0 ? 0 : a;
      if (S[b + 2] > 0.5) q.occ++;
      setOccluderFade(j, S[b], S[b + 1], S[b + 2]);
    }
  }

  get targetX(): number { return this.target.x; }

  /** RPG-1 카메라 헌법: 타격·KO 줌 펄스는 쓰지 않는다. 공유 렌더 이벤트 호출의 호환만 유지한다. */
  pushIn(_scale: number, _inMs: number, _outMs: number): void {}
  get targetZ(): number { return this.target.z; }
}
